import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Sparkles,
  Building2,
  UserCheck,
  Check,
  Save,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  FileText,
  Shield,
  ClipboardList,
  Paperclip,
  Eye,
  Send,
  Trash2,
  Plus,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

// ====================== CATÁLOGOS ======================
const ACT_TYPES = [
  { value: 'RECOMENDACAO', label: 'Recomendação' },
  { value: 'DETERMINACAO', label: 'Determinação' },
  { value: 'DILIGENCIA', label: 'Diligência' },
  { value: 'AUDITORIA_INTERNA', label: 'Auditoria Interna' },
  { value: 'AUDITORIA_EXTERNA', label: 'Auditoria Externa' },
  { value: 'TAC', label: 'TAC' },
  { value: 'ACP', label: 'ACP' },
  { value: 'SOLICITACAO_INFORMACAO', label: 'Solicitação de Informação' },
  { value: 'RELATORIO_AUDITORIA', label: 'Relatório de Auditoria' },
  { value: 'NOTIFICACAO', label: 'Notificação' },
];
const SEVERITIES = [
  { value: 'LOW', label: 'Baixa', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'MEDIUM', label: 'Média', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'HIGH', label: 'Alta', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'CRITICAL', label: 'Crítica', color: 'bg-red-100 text-red-700 border-red-200' },
];
const RISK_LEVELS = [
  { value: 'LOW', label: 'Baixo' },
  { value: 'MEDIUM', label: 'Médio' },
  { value: 'HIGH', label: 'Alto' },
  { value: 'CRITICAL', label: 'Crítico' },
];
const CONTROL_BODIES = ['TCE', 'MP', 'CGE', 'CGU', 'TCU', 'Auditoria Interna', 'Auditoria Externa', 'Outros'];
const INTERNAL_UNITS = [
  { id: 'unit_gabinete', name: 'Gabinete' },
  { id: 'unit_secretaria_geral', name: 'Secretaria Geral' },
  { id: 'unit_juridico', name: 'Assessoria Jurídica' },
  { id: 'unit_controladoria', name: 'Controladoria Interna' },
  { id: 'unit_financeiro', name: 'Departamento Financeiro' },
  { id: 'unit_administrativo', name: 'Departamento Administrativo' },
  { id: 'unit_ti', name: 'Tecnologia da Informação' },
  { id: 'unit_rh', name: 'Recursos Humanos' },
  { id: 'unit_compras', name: 'Departamento de Compras' },
  { id: 'unit_patrimonio', name: 'Patrimônio e Almoxarifado' },
];

// Catálogo de tipos de evidência esperados (informativo no passo 6)
const EVIDENCE_TYPES = [
  { id: 'DOC_OFICIAL', label: 'Documento Oficial', desc: 'Ofícios, portarias, decretos, atos administrativos.' },
  { id: 'COMPROVANTE', label: 'Comprovante de Execução', desc: 'Notas fiscais, empenhos, recibos, comprovantes de pagamento.' },
  { id: 'RELATORIO', label: 'Relatório Técnico', desc: 'Relatórios de execução, pareceres técnicos, laudos.' },
  { id: 'ATA', label: 'Ata / Termo', desc: 'Atas de reunião, termos de compromisso, atestados.' },
  { id: 'IMAGEM', label: 'Imagem / Mídia', desc: 'Fotos, vídeos, registros visuais de execução.' },
];

// Passos do Wizard
const STEPS = [
  { id: 1, key: 'origem', label: 'Origem', icon: Building2, desc: 'Órgão emissor e número' },
  { id: 2, key: 'tipo', label: 'Tipo de Ato', icon: FileText, desc: 'Natureza e área temática' },
  { id: 3, key: 'prazo', label: 'Prazo & Risco', icon: Calendar, desc: 'Deadline, severidade e risco' },
  { id: 4, key: 'responsaveis', label: 'Responsáveis', icon: UserCheck, desc: 'Unidade e servidor responsável' },
  { id: 5, key: 'plano', label: 'Plano de Ação', icon: ClipboardList, desc: 'Descrição, escopo e plano inicial' },
  { id: 6, key: 'evidencias', label: 'Evidências', icon: Paperclip, desc: 'Tipos previstos de comprovação' },
  { id: 7, key: 'revisao', label: 'Revisão', icon: Eye, desc: 'Confira todos os dados' },
  { id: 8, key: 'confirmacao', label: 'Confirmação', icon: Send, desc: 'Criação do ato' },
];

const STORAGE_KEY = 'bluegov_wizard_create_act_draft_v1';

const emptyForm = {
  // Origem
  control_body: '',
  act_number: '',
  // Tipo
  type: '',
  thematic_area: '',
  // Prazo & Risco
  deadline: '',
  severity: 'MEDIUM',
  risk_level: 'MEDIUM',
  is_judicialized: false,
  is_recurring: false,
  // Responsáveis
  responsible_unit_id: '',
  responsible_user_id: '',
  // Plano
  description: '',
  scope: '',
  initial_action: { enabled: false, action_description: '', responsible_unit: '', deadline: '' },
  // Evidências
  expected_evidence: [],
};

// ====================== VALIDAÇÃO POR STEP ======================
function validateStep(step, f) {
  const e = {};
  if (step === 1) {
    if (!f.control_body) e.control_body = 'Selecione o órgão emissor';
  }
  if (step === 2) {
    if (!f.type) e.type = 'Selecione o tipo do ato';
  }
  if (step === 3) {
    if (!f.deadline) e.deadline = 'Informe o prazo';
    else {
      const dl = new Date(f.deadline);
      if (Number.isNaN(dl.getTime())) e.deadline = 'Data inválida';
    }
  }
  if (step === 4) {
    if (!f.responsible_unit_id) e.responsible_unit_id = 'Selecione a unidade responsável';
    if (!f.responsible_user_id) e.responsible_user_id = 'Selecione o responsável';
  }
  if (step === 5) {
    if (!f.description || f.description.trim().length < 10)
      e.description = 'Descreva o ato (mínimo 10 caracteres)';
    if (f.initial_action?.enabled) {
      if (!f.initial_action.action_description || f.initial_action.action_description.trim().length < 5)
        e.action_description = 'Descreva a ação (mínimo 5 caracteres)';
      if (!f.initial_action.responsible_unit)
        e.action_unit = 'Defina a unidade da ação';
      if (!f.initial_action.deadline) e.action_deadline = 'Prazo da ação obrigatório';
    }
  }
  return e;
}

// ====================== COMPONENTE PRINCIPAL ======================
export const CreateActPage = () => {
  const navigate = useNavigate();
  const { api, canCreateAct, user } = useAuth();
  // Lazy initialization — carrega rascunho do localStorage no primeiro render
  const initial = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.formData) {
          return {
            step: parsed.step || 1,
            formData: { ...emptyForm, ...parsed.formData },
            hadDraft: true,
          };
        }
      }
    } catch {
      /* ignore */
    }
    return { step: 1, formData: emptyForm, hadDraft: false };
  })();
  const [step, setStep] = useState(initial.step);
  const [formData, setFormData] = useState(initial.formData);
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const autosaveTimer = useRef(null);

  // RBAC guard
  useEffect(() => {
    if (user && !canCreateAct()) {
      toast.error('Acesso restrito a CGE, Controle Interno e Auditoria.');
      navigate('/dashboard', { replace: true });
    }
  }, [user, canCreateAct, navigate]);

  // Notificar restauração de rascunho (sem alterar estado dentro do effect)
  useEffect(() => {
    if (initial.hadDraft) {
      toast.success('Rascunho restaurado', { duration: 2200 });
    }
  }, []);

  // Carregar usuários atribuíveis (responsável)
  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/users/assignable');
        const list = Array.isArray(response.data)
          ? response.data
          : response.data.users || [];
        setUsers(list);
      } catch (error) {
        // Fallback ao endpoint legado /users (admins)
        try {
          const fallback = await api.get('/users');
          const list = Array.isArray(fallback.data)
            ? fallback.data
            : fallback.data.users || [];
          setUsers(list);
        } catch (err2) {
          console.error('Error loading users:', err2);
          toast.error('Não foi possível carregar a lista de servidores. Contate o admin.');
        }
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, [api]);

  // Autosave debounce (700ms)
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, step }));
        setSavedAt(new Date());
      } catch {
        /* ignore */
      }
    }, 700);
    return () => clearTimeout(autosaveTimer.current);
  }, [formData, step]);

  // Handlers
  const update = useCallback((patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  }, []);

  const updateAction = useCallback((patch) => {
    setFormData((prev) => ({ ...prev, initial_action: { ...prev.initial_action, ...patch } }));
  }, []);

  const toggleEvidence = useCallback((id) => {
    setFormData((prev) => {
      const set = new Set(prev.expected_evidence);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, expected_evidence: Array.from(set) };
    });
  }, []);

  const handleAIClassify = async () => {
    if (!formData.description) {
      toast.error('Preencha a descrição no passo "Plano de Ação" para classificar com IA');
      return;
    }
    setClassifying(true);
    try {
      const response = await api.post('/ai/classify', null, {
        params: { description: formData.description },
      });
      if (response.data.error) {
        toast.error('Erro na classificação: ' + response.data.error);
      } else {
        update({
          type: response.data.suggested_type || response.data.type || formData.type,
          severity: response.data.suggested_severity || response.data.severity || formData.severity,
          risk_level: response.data.suggested_risk || response.data.risk_level || formData.risk_level,
          thematic_area: response.data.thematic_area || formData.thematic_area,
        });
        toast.success('Sugestões da IA aplicadas');
      }
    } catch (error) {
      console.error('Error classifying:', error);
      toast.error('Erro ao classificar com IA');
    } finally {
      setClassifying(false);
    }
  };

  const goNext = () => {
    const e = validateStep(step, formData);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error('Revise os campos destacados antes de avançar');
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length));
  };

  const goPrev = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 1));
  };

  const jumpTo = (target) => {
    if (target <= step) {
      setErrors({});
      setStep(target);
      return;
    }
    // valida todos passos até target-1
    for (let s = step; s < target; s += 1) {
      const e = validateStep(s, formData);
      if (Object.keys(e).length > 0) {
        setErrors(e);
        setStep(s);
        toast.error(`Complete o passo ${s} para avançar`);
        return;
      }
    }
    setStep(target);
  };

  const discardDraft = () => {
    if (!window.confirm('Descartar o rascunho atual?')) return;
    localStorage.removeItem(STORAGE_KEY);
    setFormData(emptyForm);
    setStep(1);
    setErrors({});
    toast.success('Rascunho descartado');
  };

  const submit = async () => {
    // valida tudo
    for (let s = 1; s <= 5; s += 1) {
      const e = validateStep(s, formData);
      if (Object.keys(e).length > 0) {
        setErrors(e);
        setStep(s);
        toast.error(`Complete o passo ${s} antes de confirmar`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        type: formData.type,
        control_body: formData.control_body,
        act_number: formData.act_number || null,
        severity: formData.severity,
        deadline: new Date(formData.deadline).toISOString(),
        thematic_area: formData.thematic_area || null,
        scope: formData.scope || null,
        description: formData.description,
        risk_level: formData.risk_level,
        is_judicialized: !!formData.is_judicialized,
        is_recurring: !!formData.is_recurring,
        responsible_unit_id: formData.responsible_unit_id,
        responsible_user_id: formData.responsible_user_id,
      };
      const res = await api.post('/acts', payload);
      const actId = res.data.act_id;

      // Plano inicial opcional
      if (formData.initial_action?.enabled) {
        try {
          await api.post(`/acts/${actId}/plans`, {
            action_description: formData.initial_action.action_description,
            responsible_unit: formData.initial_action.responsible_unit,
            responsible_user: null,
            deadline: new Date(formData.initial_action.deadline).toISOString(),
            evidence_required: true,
          });
        } catch (err) {
          console.warn('Falha ao criar plano inicial', err);
          toast.warning('Ato criado, mas o plano inicial não pôde ser cadastrado. Adicione manualmente.');
        }
      }

      localStorage.removeItem(STORAGE_KEY);
      toast.success('Ato de controle criado com sucesso');
      navigate(`/acts/${actId}`);
    } catch (error) {
      console.error('Erro ao criar ato:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar ato de controle');
      setSubmitting(false);
    }
  };

  const progress = useMemo(() => Math.round(((step - 1) / (STEPS.length - 1)) * 100), [step]);
  const currentStepMeta = STEPS[step - 1];

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto" data-testid="create-act-wizard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/acts')} data-testid="wizard-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Novo Ato de Controle</h1>
              <p className="text-slate-500 text-sm">Wizard institucional · 8 etapas · Autosave</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {savedAt && (
              <Badge variant="outline" className="gap-1 text-emerald-700 border-emerald-200 bg-emerald-50" data-testid="autosave-indicator">
                <Save className="w-3 h-3" /> Rascunho salvo{' '}
                {savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={discardDraft}
              className="text-slate-500 hover:text-red-600"
              data-testid="discard-draft-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Descartar
            </Button>
          </div>
        </div>

        {/* Stepper */}
        <Card className="mb-6 border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-2 mb-4 overflow-x-auto pb-2">
              {STEPS.map((s) => {
                const done = step > s.id;
                const active = step === s.id;
                const Icon = s.icon;
                return (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => jumpTo(s.id)}
                    className={`flex flex-col items-center min-w-[88px] px-2 py-2 rounded-lg transition-all ${
                      active ? 'bg-[#1d4ed8]/10' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`step-nav-${s.id}`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center mb-1 border-2 transition-all ${
                        done
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : active
                          ? 'bg-[#1d4ed8] border-[#1d4ed8] text-white shadow-lg shadow-blue-500/30'
                          : 'bg-white border-slate-200 text-slate-400'
                      }`}
                    >
                      {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span
                      className={`text-[11px] font-semibold whitespace-nowrap ${
                        active ? 'text-[#1d4ed8]' : done ? 'text-emerald-700' : 'text-slate-500'
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <Progress value={progress} className="h-1.5" data-testid="wizard-progress" />
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-slate-500">
                Passo <strong className="text-slate-700">{step}</strong> de {STEPS.length}: {currentStepMeta.desc}
              </p>
              <p className="text-xs text-slate-400">{progress}% concluído</p>
            </div>
          </CardContent>
        </Card>

        {/* Step body */}
        <Card className="mb-6 border-slate-200" data-testid={`step-panel-${step}`}>
          <CardContent className="pt-6 pb-6">
            {step === 1 && <StepOrigem f={formData} errors={errors} update={update} />}
            {step === 2 && (
              <StepTipo
                f={formData}
                errors={errors}
                update={update}
                onClassifyAI={handleAIClassify}
                classifying={classifying}
              />
            )}
            {step === 3 && <StepPrazo f={formData} errors={errors} update={update} />}
            {step === 4 && (
              <StepResponsaveis
                f={formData}
                errors={errors}
                update={update}
                users={users}
                loadingUsers={loadingUsers}
              />
            )}
            {step === 5 && (
              <StepPlano
                f={formData}
                errors={errors}
                update={update}
                updateAction={updateAction}
                onClassifyAI={handleAIClassify}
                classifying={classifying}
              />
            )}
            {step === 6 && <StepEvidencias f={formData} toggle={toggleEvidence} />}
            {step === 7 && <StepRevisao f={formData} jumpTo={jumpTo} users={users} />}
            {step === 8 && <StepConfirmacao f={formData} submit={submit} submitting={submitting} />}
          </CardContent>
        </Card>

        {/* Footer Nav */}
        <div className="flex items-center justify-between mb-12">
          <Button
            variant="outline"
            onClick={goPrev}
            disabled={step === 1 || submitting}
            data-testid="wizard-prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
          </Button>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/acts')}
              disabled={submitting}
              data-testid="wizard-cancel-btn"
            >
              Cancelar
            </Button>
            {step < STEPS.length && (
              <Button
                onClick={goNext}
                className="bg-[#1d4ed8] hover:bg-[#1e40af]"
                disabled={submitting}
                data-testid="wizard-next-btn"
              >
                Avançar <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {/* Step 8 já contém o botão principal de confirmação (wizard-confirm-final-btn) */}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

// ====================== SUB-COMPONENTES POR STEP ======================

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-6 pb-4 border-b border-slate-100">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1d4ed8] to-[#1e40af] flex items-center justify-center text-white shadow-md shadow-blue-500/20">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
  </div>
);

const ErrorMsg = ({ msg }) =>
  msg ? (
    <p className="text-xs text-red-600 mt-1 flex items-center gap-1" role="alert">
      <AlertTriangle className="w-3 h-3" /> {msg}
    </p>
  ) : null;

// ---- STEP 1: ORIGEM ----
function StepOrigem({ f, errors, update }) {
  return (
    <>
      <SectionHeader icon={Building2} title="Origem do Ato" subtitle="Identifique o órgão emissor e o número oficial do documento" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="font-semibold">Órgão Emissor *</Label>
          <Select value={f.control_body} onValueChange={(v) => update({ control_body: v })}>
            <SelectTrigger data-testid="wizard-control-body">
              <SelectValue placeholder="Selecione o órgão" />
            </SelectTrigger>
            <SelectContent>
              {CONTROL_BODIES.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorMsg msg={errors.control_body} />
        </div>
        <div>
          <Label className="font-semibold">Número do Ato</Label>
          <Input
            value={f.act_number}
            onChange={(e) => update({ act_number: e.target.value })}
            placeholder="Ex: Acórdão 1234/2024-TCE"
            data-testid="wizard-act-number"
          />
          <p className="text-xs text-slate-500 mt-1">Número oficial do documento (opcional)</p>
        </div>
      </div>
      <div className="mt-6 rounded-lg bg-blue-50 border border-blue-200 p-3 flex gap-2">
        <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-800">
          A origem identifica formalmente o órgão de controle que emitiu o documento. Esta informação será exibida no Dossiê Executivo e no Portal de Conformidade.
        </p>
      </div>
    </>
  );
}

// ---- STEP 2: TIPO ----
function StepTipo({ f, errors, update, onClassifyAI, classifying }) {
  return (
    <>
      <SectionHeader icon={FileText} title="Tipo de Ato" subtitle="Classifique a natureza do ato e a área temática envolvida" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <div className="flex items-center justify-between">
            <Label className="font-semibold">Tipo de Ato *</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClassifyAI}
              disabled={classifying || !f.description}
              data-testid="wizard-ai-classify-btn"
              className="text-[#1d4ed8] hover:text-[#1e40af]"
            >
              {classifying ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 mr-1" />
              )}
              Sugerir com IA
            </Button>
          </div>
          <Select value={f.type} onValueChange={(v) => update({ type: v })}>
            <SelectTrigger data-testid="wizard-type">
              <SelectValue placeholder="Selecione o tipo" />
            </SelectTrigger>
            <SelectContent>
              {ACT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorMsg msg={errors.type} />
        </div>
        <div>
          <Label className="font-semibold">Área Temática</Label>
          <Input
            value={f.thematic_area}
            onChange={(e) => update({ thematic_area: e.target.value })}
            placeholder="Ex: Saúde, Educação, Infraestrutura"
            data-testid="wizard-thematic"
          />
          <p className="text-xs text-slate-500 mt-1">Sugestão da IA pode preencher automaticamente.</p>
        </div>
      </div>
    </>
  );
}

// ---- STEP 3: PRAZO ----
function StepPrazo({ f, errors, update }) {
  const today = new Date().toISOString().slice(0, 10);
  const dl = f.deadline ? new Date(f.deadline) : null;
  const diffDays = dl ? Math.ceil((dl - new Date()) / (1000 * 60 * 60 * 24)) : null;
  return (
    <>
      <SectionHeader icon={Calendar} title="Prazo & Risco" subtitle="Defina o prazo legal, severidade e nível de risco do ato" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="font-semibold">Prazo Limite *</Label>
          <Input
            type="date"
            min={today}
            value={f.deadline}
            onChange={(e) => update({ deadline: e.target.value })}
            data-testid="wizard-deadline"
          />
          {diffDays !== null && (
            <p
              className={`text-xs mt-1 font-medium ${
                diffDays < 7 ? 'text-red-600' : diffDays < 30 ? 'text-amber-600' : 'text-emerald-600'
              }`}
            >
              {diffDays >= 0 ? `${diffDays} dia(s) até o prazo` : `${Math.abs(diffDays)} dia(s) em atraso`}
            </p>
          )}
          <ErrorMsg msg={errors.deadline} />
        </div>
        <div>
          <Label className="font-semibold">Severidade</Label>
          <div className="grid grid-cols-4 gap-2 mt-1">
            {SEVERITIES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => update({ severity: s.value })}
                className={`px-2 py-2 rounded-lg border text-xs font-bold uppercase tracking-wide transition-all ${
                  f.severity === s.value ? s.color + ' ring-2 ring-offset-1 ring-current' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                }`}
                data-testid={`wizard-severity-${s.value}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="font-semibold">Nível de Risco</Label>
          <Select value={f.risk_level} onValueChange={(v) => update({ risk_level: v })}>
            <SelectTrigger data-testid="wizard-risk">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RISK_LEVELS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-3 pt-1">
          <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
            <Switch
              checked={f.is_judicialized}
              onCheckedChange={(v) => update({ is_judicialized: v })}
              data-testid="wizard-judicialized"
            />
            <div>
              <p className="text-sm font-semibold text-slate-800">Ato Judicializado</p>
              <p className="text-xs text-slate-500">Marque se há processo judicial em curso</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
            <Switch
              checked={f.is_recurring}
              onCheckedChange={(v) => update({ is_recurring: v })}
              data-testid="wizard-recurring"
            />
            <div>
              <p className="text-sm font-semibold text-slate-800">Ato Reincidente</p>
              <p className="text-xs text-slate-500">Marque se já houve ato similar anterior</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ---- STEP 4: RESPONSÁVEIS ----
function StepResponsaveis({ f, errors, update, users, loadingUsers }) {
  return (
    <>
      <SectionHeader icon={UserCheck} title="Responsabilidade" subtitle="Defina quem responderá institucionalmente pelo ato" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label className="font-semibold flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-slate-500" /> Unidade Responsável *
          </Label>
          <Select value={f.responsible_unit_id} onValueChange={(v) => update({ responsible_unit_id: v })}>
            <SelectTrigger data-testid="wizard-unit">
              <SelectValue placeholder="Selecione a unidade" />
            </SelectTrigger>
            <SelectContent>
              {INTERNAL_UNITS.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorMsg msg={errors.responsible_unit_id} />
        </div>
        <div>
          <Label className="font-semibold flex items-center gap-1.5">
            <UserCheck className="w-3.5 h-3.5 text-slate-500" /> Servidor Responsável *
          </Label>
          <Select
            value={f.responsible_user_id}
            onValueChange={(v) => update({ responsible_user_id: v })}
            disabled={loadingUsers}
          >
            <SelectTrigger data-testid="wizard-user">
              <SelectValue placeholder={loadingUsers ? 'Carregando…' : 'Selecione o responsável'} />
            </SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>
                  {u.name} ({u.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ErrorMsg msg={errors.responsible_user_id} />
        </div>
      </div>
      <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          O servidor responsável receberá uma notificação automática e será incluído em todos os logs de auditoria do ato.
        </p>
      </div>
    </>
  );
}

// ---- STEP 5: PLANO ----
function StepPlano({ f, errors, update, updateAction, onClassifyAI, classifying }) {
  return (
    <>
      <SectionHeader icon={ClipboardList} title="Plano de Ação" subtitle="Descreva o ato, o escopo e, opcionalmente, a primeira ação a executar" />
      <div className="space-y-5">
        <div>
          <div className="flex items-center justify-between">
            <Label className="font-semibold">Descrição do Ato *</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClassifyAI}
              disabled={classifying || !f.description}
              data-testid="wizard-ai-step5-btn"
              className="text-[#1d4ed8] hover:text-[#1e40af]"
            >
              {classifying ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 mr-1" />
              )}
              Classificar com IA
            </Button>
          </div>
          <Textarea
            rows={4}
            value={f.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Descreva detalhadamente o ato recebido — fatos, exigências, fundamentação legal…"
            data-testid="wizard-description"
          />
          <div className="flex items-center justify-between mt-1">
            <ErrorMsg msg={errors.description} />
            <p className="text-xs text-slate-400">{f.description.length} caracteres</p>
          </div>
        </div>
        <div>
          <Label className="font-semibold">Escopo</Label>
          <Textarea
            rows={3}
            value={f.scope}
            onChange={(e) => update({ scope: e.target.value })}
            placeholder="Defina o escopo de aplicação do ato (unidades, processos, período)…"
            data-testid="wizard-scope"
          />
        </div>

        <div className="rounded-xl border-2 border-dashed border-slate-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-semibold text-slate-800 flex items-center gap-2">
                <Plus className="w-4 h-4 text-[#1d4ed8]" /> Cadastrar 1ª ação do plano (opcional)
              </p>
              <p className="text-xs text-slate-500">
                Você pode adicionar mais ações depois, dentro do ato.
              </p>
            </div>
            <Switch
              checked={f.initial_action.enabled}
              onCheckedChange={(v) => updateAction({ enabled: v })}
              data-testid="wizard-initial-action-toggle"
            />
          </div>

          {f.initial_action.enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-100">
              <div className="md:col-span-2">
                <Label className="font-semibold">Descrição da Ação *</Label>
                <Textarea
                  rows={2}
                  value={f.initial_action.action_description}
                  onChange={(e) => updateAction({ action_description: e.target.value })}
                  placeholder="Ex: Emitir parecer técnico sobre conformidade do contrato XYZ"
                  data-testid="wizard-action-desc"
                />
                <ErrorMsg msg={errors.action_description} />
              </div>
              <div>
                <Label className="font-semibold">Unidade Executora *</Label>
                <Select
                  value={f.initial_action.responsible_unit}
                  onValueChange={(v) => updateAction({ responsible_unit: v })}
                >
                  <SelectTrigger data-testid="wizard-action-unit">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERNAL_UNITS.map((u) => (
                      <SelectItem key={u.id} value={u.name}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <ErrorMsg msg={errors.action_unit} />
              </div>
              <div>
                <Label className="font-semibold">Prazo da Ação *</Label>
                <Input
                  type="date"
                  value={f.initial_action.deadline}
                  onChange={(e) => updateAction({ deadline: e.target.value })}
                  data-testid="wizard-action-deadline"
                />
                <ErrorMsg msg={errors.action_deadline} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ---- STEP 6: EVIDÊNCIAS ----
function StepEvidencias({ f, toggle }) {
  return (
    <>
      <SectionHeader icon={Paperclip} title="Evidências Previstas" subtitle="Indique quais tipos de comprovação serão exigidos para encerrar o ato" />
      <p className="text-sm text-slate-600 mb-4">
        Selecione os tipos de evidência que deverão ser anexados durante a execução. Esta lista guiará a checagem de
        conformidade. O upload dos arquivos é feito após a criação, dentro do ato.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {EVIDENCE_TYPES.map((ev) => {
          const checked = f.expected_evidence.includes(ev.id);
          return (
            <button
              type="button"
              key={ev.id}
              onClick={() => toggle(ev.id)}
              className={`text-left rounded-xl border-2 p-4 transition-all ${
                checked
                  ? 'border-[#1d4ed8] bg-blue-50 shadow-md shadow-blue-500/10'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid={`wizard-evidence-${ev.id}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-6 h-6 rounded-md border-2 flex items-center justify-center mt-0.5 shrink-0 ${
                    checked ? 'bg-[#1d4ed8] border-[#1d4ed8] text-white' : 'border-slate-300'
                  }`}
                >
                  {checked && <Check className="w-3.5 h-3.5" />}
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{ev.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{ev.desc}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-5 rounded-lg bg-blue-50 border border-blue-200 p-3 flex gap-2">
        <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-800">
          Esta seleção é orientativa — durante a execução, qualquer tipo de evidência pode ser anexado conforme necessidade.
        </p>
      </div>
    </>
  );
}

// ---- STEP 7: REVISÃO ----
function StepRevisao({ f, jumpTo, users }) {
  const userName = users.find((u) => u.user_id === f.responsible_user_id)?.name || '—';
  const unitName = INTERNAL_UNITS.find((u) => u.id === f.responsible_unit_id)?.name || '—';
  const typeLabel = ACT_TYPES.find((t) => t.value === f.type)?.label || '—';
  const sev = SEVERITIES.find((s) => s.value === f.severity);
  const risk = RISK_LEVELS.find((r) => r.value === f.risk_level)?.label || '—';

  const sections = [
    {
      step: 1,
      title: 'Origem',
      icon: Building2,
      rows: [
        ['Órgão Emissor', f.control_body || '—'],
        ['Número do Ato', f.act_number || '—'],
      ],
    },
    {
      step: 2,
      title: 'Tipo de Ato',
      icon: FileText,
      rows: [
        ['Tipo', typeLabel],
        ['Área Temática', f.thematic_area || '—'],
      ],
    },
    {
      step: 3,
      title: 'Prazo & Risco',
      icon: Calendar,
      rows: [
        ['Prazo', f.deadline ? new Date(f.deadline).toLocaleDateString('pt-BR') : '—'],
        ['Severidade', sev?.label || '—'],
        ['Nível de Risco', risk],
        ['Judicializado', f.is_judicialized ? 'Sim' : 'Não'],
        ['Reincidente', f.is_recurring ? 'Sim' : 'Não'],
      ],
    },
    {
      step: 4,
      title: 'Responsáveis',
      icon: UserCheck,
      rows: [
        ['Unidade', unitName],
        ['Servidor', userName],
      ],
    },
    {
      step: 5,
      title: 'Plano de Ação',
      icon: ClipboardList,
      rows: [
        ['Descrição', f.description || '—', true],
        ['Escopo', f.scope || '—', true],
        ...(f.initial_action?.enabled
          ? [
              ['1ª Ação', f.initial_action.action_description || '—', true],
              ['Unidade da Ação', f.initial_action.responsible_unit || '—'],
              ['Prazo da Ação', f.initial_action.deadline ? new Date(f.initial_action.deadline).toLocaleDateString('pt-BR') : '—'],
            ]
          : []),
      ],
    },
    {
      step: 6,
      title: 'Evidências Previstas',
      icon: Paperclip,
      rows: [
        [
          'Tipos',
          f.expected_evidence.length === 0
            ? 'Nenhuma selecionada'
            : f.expected_evidence
                .map((id) => EVIDENCE_TYPES.find((e) => e.id === id)?.label)
                .filter(Boolean)
                .join(', '),
          true,
        ],
      ],
    },
  ];

  return (
    <>
      <SectionHeader icon={Eye} title="Revisão Final" subtitle="Verifique todos os dados antes de confirmar a criação do ato" />
      <div className="space-y-4">
        {sections.map((sec) => {
          const Icon = sec.icon;
          return (
            <div key={sec.step} className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
              <div className="flex items-center justify-between mb-3">
                <p className="font-bold text-slate-800 flex items-center gap-2">
                  <Icon className="w-4 h-4 text-[#1d4ed8]" /> {sec.title}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => jumpTo(sec.step)}
                  className="text-[#1d4ed8] hover:text-[#1e40af] h-7"
                  data-testid={`review-edit-${sec.step}`}
                >
                  Editar
                </Button>
              </div>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {sec.rows.map(([k, v, wide], i) => (
                  <div key={i} className={wide ? 'md:col-span-2' : ''}>
                    <dt className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{k}</dt>
                    <dd className="text-slate-800 mt-0.5 whitespace-pre-wrap">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ---- STEP 8: CONFIRMAÇÃO ----
function StepConfirmacao({ f, submit, submitting }) {
  return (
    <div className="text-center py-6">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white mb-4 shadow-xl shadow-emerald-500/30">
        <Shield className="w-10 h-10" />
      </div>
      <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-2">Pronto para registrar</h2>
      <p className="text-slate-600 max-w-xl mx-auto mb-6">
        Ao confirmar, o ato será registrado no sistema, vinculado à trilha de auditoria e o servidor responsável será
        notificado automaticamente.
      </p>
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Auditoria registrada</Badge>
        <Badge className="bg-blue-100 text-blue-700 border-blue-200">Notificação automática</Badge>
        {f.initial_action?.enabled && (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200">Plano inicial será criado</Badge>
        )}
        <Badge className="bg-amber-100 text-amber-700 border-amber-200">
          Prazo: {f.deadline ? new Date(f.deadline).toLocaleDateString('pt-BR') : '—'}
        </Badge>
      </div>
      <Button
        onClick={submit}
        disabled={submitting}
        size="lg"
        className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
        data-testid="wizard-confirm-final-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
        Confirmar e Criar Ato
      </Button>
      <p className="text-xs text-slate-400 mt-4">
        Você poderá editar o ato e adicionar mais ações depois, dentro da página do ato.
      </p>
    </div>
  );
}

export default CreateActPage;
